/**
 * Shopify Lazy Loader v3
 * Fixed: IntersectionObserver not firing for in-viewport elements on load
 * Fixed: Handles Shopify's fallback <img> inside <video>
 * Fixed: Skips <source> elements that are already resolved (src set, no data-src)
 */

(function () {
  'use strict';

  const SELECTOR     = 'video[class*="lazyload"], img[data-src], img[data-srcset], source[data-src], source[data-srcset], iframe[data-src]';
  const LOADED_CLASS  = 'lazyloaded';
  const LOADING_CLASS = 'lazyloading';
  const ERROR_CLASS   = 'lazyfailed';

  // Start loading 300px before it enters viewport
  const observerOptions = {
    root: null,
    rootMargin: '300px 0px',
    threshold: 0,
  };

  let observer;

  function getObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(onIntersect, observerOptions);
    return observer;
  }

  // --- Load a single element ---

  function loadElement(el) {
    if (el.classList.contains(LOADED_CLASS)) return;
    el.classList.remove(ERROR_CLASS);

    const tag = el.tagName.toLowerCase();

    if (tag === 'video') {
      loadVideo(el);
      return;
    }

    if (tag === 'iframe') {
      loadIframe(el);
      return;
    }

    // <source> — only if it still has data-src
    if (tag === 'source') {
      if (el.dataset.src)    { el.src    = el.dataset.src;    delete el.dataset.src; }
      if (el.dataset.srcset) { el.srcset = el.dataset.srcset; delete el.dataset.srcset; }
      const parent = el.closest('video');
      if (parent) parent.load();
      el.classList.add(LOADED_CLASS);
      return;
    }

    // <img> — skip if nested inside <video> (Shopify fallback img)
    if (tag === 'img') {
      if (el.closest('video')) return;
      loadImg(el);
    }
  }

  function loadVideo(el) {
    el.classList.add(LOADING_CLASS);

    // Strip preload so browser doesn't fetch on its own
    el.removeAttribute('preload');

    // Swap all <source data-src> inside this video
    let swapped = false;
    el.querySelectorAll('source[data-src]').forEach((s) => {
      s.src = s.dataset.src;
      delete s.dataset.src;
      swapped = true;
    });
    el.querySelectorAll('source[data-srcset]').forEach((s) => {
      s.srcset = s.dataset.srcset;
      delete s.dataset.srcset;
      swapped = true;
    });

    // Swap fallback <img> inside video
    el.querySelectorAll('img[data-src]').forEach((img) => {
      img.src = img.dataset.src;
      delete img.dataset.src;
    });

    // Direct src on video element
    if (el.dataset.src) {
      el.src = el.dataset.src;
      delete el.dataset.src;
      swapped = true;
    }

    if (!swapped) {
      // Nothing to swap — already loaded or wrong setup
      el.classList.remove(LOADING_CLASS);
      el.classList.add(LOADED_CLASS);
      return;
    }

    el.addEventListener('canplay', () => onMediaLoaded(el), { once: true });
    el.addEventListener('error',   () => onMediaError(el),  { once: true });
    el.load();
  }

  function loadImg(el) {
    el.classList.add(LOADING_CLASS);
    el.addEventListener('load',  () => onMediaLoaded(el), { once: true });
    el.addEventListener('error', () => onMediaError(el),  { once: true });
    if (el.dataset.srcset) { el.srcset = el.dataset.srcset; delete el.dataset.srcset; }
    if (el.dataset.src)    { el.src    = el.dataset.src;    delete el.dataset.src; }
  }

  function loadIframe(el) {
    el.classList.add(LOADING_CLASS);
    el.addEventListener('load',  () => onMediaLoaded(el), { once: true });
    el.addEventListener('error', () => onMediaError(el),  { once: true });
    el.src = el.dataset.src;
    delete el.dataset.src;
  }

  function onMediaLoaded(el) {
    el.classList.remove(LOADING_CLASS);
    el.classList.add(LOADED_CLASS);
    el.dispatchEvent(new CustomEvent('lazyloaded', { bubbles: true }));
  }

  function onMediaError(el) {
    el.classList.remove(LOADING_CLASS);
    el.classList.add(ERROR_CLASS);
    el.dispatchEvent(new CustomEvent('lazyfailed', { bubbles: true }));
    console.warn('[LazyLoad] Failed to load:', el);
  }

  // --- IntersectionObserver callback ---

  function onIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      getObserver().unobserve(el);
      loadElement(el);
    });
  }

  // --- Observe a single element ---

  function observe(el) {
    if (!(el instanceof Element)) return;
    if (el.classList.contains(LOADED_CLASS)) return;

    // No IntersectionObserver support → load immediately
    if (!('IntersectionObserver' in window)) {
      loadElement(el);
      return;
    }

    getObserver().observe(el);
  }

  // --- Scan DOM for lazy elements ---

  function refresh(root) {
    const scope = root || document;

    // Collect target elements:
    // 1. Videos with lazyload class that still have unresolved sources
    scope.querySelectorAll('video.lazyload, video[class*="lazyload"]').forEach((v) => {
      const hasPendingSources = v.querySelector('source[data-src], source[data-srcset]');
      const hasPendingSrc = v.dataset.src;
      if (hasPendingSources || hasPendingSrc) {
        observe(v);
      }
    });

    // 2. Standalone lazy images (not inside video)
    scope.querySelectorAll('img[data-src], img[data-srcset]').forEach((img) => {
      if (!img.closest('video')) observe(img);
    });

    // 3. Iframes
    scope.querySelectorAll('iframe[data-src]').forEach(observe);
  }

  // --- Shopify section/block events ---

  document.addEventListener('shopify:section:load', (e) => refresh(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    if (e.target) {
      e.target.querySelectorAll('video.lazyload, img[data-src], iframe[data-src]').forEach(loadElement);
    }
  });

  // --- CSS ---

  function injectStyles() {
    if (document.getElementById('lazyload-styles')) return;
    const style = document.createElement('style');
    style.id = 'lazyload-styles';
    style.textContent = `
      video.lazyload,
      video.lazyloading {
        opacity: 0;
      }
      video.lazyloaded {
        opacity: 1;
        transition: opacity 0.4s ease;
      }
      img.lazyload,
      img.lazyloading {
        filter: blur(8px);
        transition: filter 0.4s ease;
      }
      img.lazyloaded {
        filter: blur(0);
      }
    `;
    document.head.appendChild(style);
  }

  // --- Init ---
  // Use requestIdleCallback so we don't block page render,
  // but fall back to setTimeout for Safari

  function init() {
    injectStyles();
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => refresh(document), { timeout: 1000 });
    } else {
      setTimeout(() => refresh(document), 100);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.LazyLoad = { refresh, observe, loadElement };

})();